import React, { useEffect, useMemo, useRef } from "react";
import type { IMessage } from "../../types";
import AiAvatar from "@/assets/interview/conversation/ai-avatar.svg";

const LIVE_PREVIEW_ID = Number.MAX_SAFE_INTEGER;

interface ConversationProps {
  messages: IMessage[];
  responseText?: string;
  isStreaming?: boolean;
  candidateName?: string;
}

const Conversation: React.FC<ConversationProps> = React.memo(
  ({ messages, responseText, isStreaming = false, candidateName }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    const renderedMessages = useMemo<IMessage[]>(() => {
      const baseMessages = messages.map((message) => ({ ...message }));
      const recentMessages = baseMessages.slice(-2);

      const stackedMessages: IMessage[] = [
        ...recentMessages,
      ];

      if (isStreaming && responseText && responseText.trim().length > 0) {
        stackedMessages.push({
          id: LIVE_PREVIEW_ID + 1,
          role: "AI" as const,
          message: responseText.trim(),
        });
      }

      // Show up to the last two committed messages plus live overlays (if any)
      return stackedMessages;
    }, [messages, responseText, isStreaming]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const isUserNearBottom = () => {
        const threshold = 150;
        const position =
          container.scrollHeight - container.scrollTop - container.clientHeight;
        return position <= threshold;
      };

      if (isUserNearBottom()) {
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            try {
              scrollRef.current.scrollIntoView({ behavior: "smooth" });
            } catch {}
          }
        });
      }
    }, [renderedMessages]);

    return (
      <div
        ref={containerRef}
        className="relative w-full rounded-xl h-full min-h-0"
        style={{
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch",
          backgroundColor: "#f5f5f5",
        }}
      >
        <div
          className="overflow-y-scroll max-h-full minimal-scrollbar flex flex-col-reverse p-4 md:p-0 lg:p-0 xl:p-0"
        >
          {renderedMessages.map((message, index) => {
            const isFirst = index === 0;
            const isLast = index === renderedMessages.length - 1;
            let spacingClass: string | undefined;

            if (isFirst) {
              spacingClass = "mb-4";
            } else if (isLast) {
              spacingClass = "mb-5 md:mb-2 lg:mb-2 xl:mb-2.5";
            }

            return (
              <Message
                key={`${message.id}`}
                message={{ ...message, className: spacingClass }}
                candidateName={candidateName}
              />
            );
          })}
          <div ref={scrollRef} />
        </div>
      </div>
    );
  }
);

export default Conversation;

const getInitialsFromName = (fullName?: string) => {
  if (!fullName) return "C";
  const parts = fullName
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "C";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  const first = parts[0].charAt(0);
  const last = parts[parts.length - 1].charAt(0);
  return `${first}${last}`.toUpperCase();
};

const Message: React.FC<{
  message: IMessage & { className?: string };
  candidateName?: string;
}> = React.memo(({ message, candidateName }) => {
  const userProfileImage = null; // Mocked
  const userInitials = getInitialsFromName(candidateName);

  // Build display text: for USER messages, show last 5 lines if > 8 lines
  const getDisplayText = (text: string) => {
    if (message.role === "USER") {
      const cleaned = (text || "").trim();
      if (!cleaned) return cleaned;

      const lines = cleaned.split('\n');
      if (lines.length > 8) {
        const lastFiveLines = lines.slice(-5).join('\n');
        return `...\n${lastFiveLines}`;
      }

      return cleaned;
    }
    return text;
  };

  const displayText = getDisplayText(message.message);

  // Avatar component - simplified and responsive
  const Avatar = () => {
    if (message.role === "USER") {
      if (userProfileImage) {
        return (
          <div className="w-7 h-7 md:w-8 md:h-8 lg:w-9 lg:h-9 xl:w-10 xl:h-10 rounded-full bg-white flex items-center justify-center overflow-hidden">
            <img
              src={userProfileImage}
              alt="User Profile"
              className="w-full h-full object-cover"
            />
          </div>
        );
      }
      return (
        <div className="w-7 h-7 md:w-8 md:h-8 lg:w-9 lg:h-9 xl:w-10 xl:h-10 rounded-full bg-gray-200 flex items-center justify-center">
          <span className="text-grey-6 text-sm md:text-base font-medium">
            {userInitials}
          </span>
        </div>
      );
    }

    // AI Avatar - simple circle with logo
    return (
      <div className="w-7 h-7 md:w-8 md:h-8 lg:w-9 lg:h-9 xl:w-10 xl:h-10 2xl:w-12 2xl:h-12 rounded-full bg-white flex items-center justify-center p-1.5 md:p-2">
        <img
          src={AiAvatar}
          alt="AI Interviewer"
          className="w-full h-full object-contain"
        />
      </div>
    );
  };

  return (
    <div
      className={`flex gap-2 md:gap-2.5 lg:gap-3 xl:gap-4 items-start py-1.5 px-1.5 w-full ${message.className ?? ''}`}
    >
      <div className="shrink-0">
        <Avatar />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-left font-dm-sans text-[9px] md:text-xs lg:text-sm xl:text-base leading-[14px] md:leading-4 lg:leading-5 xl:leading-6 text-slate-900 whitespace-pre-wrap">
          {displayText}
        </p>
      </div>
    </div>
  );
});
